import { BooleanAttributes, SVGElements } from './constants';
import { ComponentState } from '../components/ui-element.const';
import { isNode, isElementNode, isPlainObject } from './helpers';
import type { UIElement } from '../components/index';
import type {
    ClassNameConfig,
    NullOr,
    Optional,
    UndefinedOr,
} from './utils.type';
import type {
    PropsAttribute,
    Attributes,
    ElementConfig,
    ElementChild,
} from '../components/ui-element.type';

/**
 * Returns xmlns namespace by key
 * @memberof UI
 * @alias resolveNamespace
 * @param {string} key
 * @returns {string}
 */
const resolveNamespace = (key: string): string => {
    switch (key) {
        case 'svg':
            return 'http://www.w3.org/2000/' + key;
        case 'xlink':
            return 'http://www.w3.org/1999/' + key;
        default:
            return 'http://www.w3.org/1999/' + key;
    }
};

/**
 * Update class list of the node by given config.
 * @memberof UI
 * @alias updateClassList
 * @example
 * // adds -active class to the node classList and removes -awesome from the node classList
 * element.updateClassList({"-active": true, "-awesome": false})
 * @param {HTMLElement | UIElement} element
 * @param {Record<string, boolean>} config - hash map where key is classname value is flag
 * to add / remove it.
 * @returns {HTMLElement | UIElement}
 */
const updateClassList = (
    element: HTMLElement | UIElement | Element,
    config: ClassNameConfig
): HTMLElement | UIElement | Element => {
    Object.keys(config || {}).forEach((key) => {
        if (!!config[key] && !element.classList.contains(key)) {
            element.classList.add(key);
        }
        if (!config[key] && element.classList.contains(key)) {
            element.classList.remove(key);
        }
    });
    return element;
};

/**
 * Update class list of the node by reverting given config,
 * could be used for animations to roll back last applied class config.
 * @memberof UI
 * @alias revertClassList
 * @example
 * // adds -active class to the node classList and removes -awesome from the node classList
 * element.revertClassList({"-active": true, "-awesome": false})
 * @param {HTMLElement} element
 * @param {Record<string, boolean>} config - hash map where key is classname, value is flag
 * which defines if the class should be added or removed
 * @returns {HTMLElement}
 */
const revertClassList = (
    element: HTMLElement,
    config: ClassNameConfig
): HTMLElement | Element => {
    return updateClassList(
        element,
        Object.keys(config || {}).reduce<ClassNameConfig>((acc, className) => {
            acc[className] = !config[className];
            return acc;
        }, {})
    );
};

/**
 * @memberof UI
 * @alias setInnerText
 * @param {Element} element
 * @param {string} text
 * @param {boolean} [trusted] - Deprecated: use setUnsafeInnerHTML instead.
 * Be careful when using this method,it can cause XSS vulnerabilities if used with untrusted input.
 * @returns {Element}
 */
const setInnerText = (
    element: Element,
    text: string,
    trusted: boolean = false
): Element => {
    if (trusted) {
        return setUnsafeInnerHTML(element, text);
    }
    element.innerHTML = '';
    element.appendChild(document.createTextNode(text));
    return element;
};

/**
 * Set the attributes compatible with HTML5 specification. Where falsy boolean
 * attributes are not set in DOM, but truthy attributes are set without value.
 * @param {HTMLElement} element
 * @param {AttributeProps | Record<string, string | null>} attributes
 */
const setHTML5SpecAttributes = (
    element: HTMLElement | Element,
    attributes: PropsAttribute | Record<string, string | null>
) => {
    Object.keys(attributes || {}).forEach((key: string) => {
        const attributeValue: unknown = processAriaBooleans(
            key,
            attributes[key as keyof PropsAttribute]
        );
        const isTruthyBoolean = attributeValue === true;
        const isFalsyBoolean =
            attributeValue === false ||
            attributeValue === undefined ||
            attributeValue === null;

        if (!isFalsyBoolean) {
            if (key.includes(':')) {
                element.setAttributeNS(
                    resolveNamespace(key.split(':').shift()!),
                    key,
                    attributeValue as string
                );
            } else {
                if (isTruthyBoolean) {
                    element.setAttribute(key, '');
                } else {
                    element.setAttribute(key, attributeValue as string);
                }
            }
        } else {
            element.removeAttribute(key);
        }
    });
};

/**
 * @memberof UI
 * @alias setAttributes
 * @param {HTMLElement} element
 * @param {AttributeProps | Record<string, string | null>} attributes to be applied to the node
 * @example
 * // Set attributes "foo" and "bar" to attributes "id" and "name" respectively
 * element.setAttributes({id: "foo", name: "bar"});
 * @returns {HTMLElement}
 */
const setAttributes = (
    element: HTMLElement | Element,
    attributes: PropsAttribute | Record<string, string | null>
): HTMLElement | Element => {
    if (isPlainObject(attributes)) {
        setHTML5SpecAttributes(element, attributes);
    }
    return element;
};

/**
 * Processes safely aria-* boolean attributes.
 * @param {string} attributeName
 * @param {*} attributeValue
 * @returns {string|*}
 */
const processAriaBooleans = (
    attributeName: string,
    attributeValue: unknown
): unknown => {
    return attributeName.startsWith('aria-') &&
        typeof attributeValue === 'boolean'
        ? String(attributeValue)
        : attributeValue;
};

/**
 * Removes the attributes from a given element.
 * @param {HTMLElement} element
 * @param {NodeList | string[]} attributes list of attributes to be removed
 */
const removeAttributes = (
    element: HTMLElement,
    attributes: NodeList | Array<string>
) => {
    [].forEach.call(attributes || [], (key) => element.removeAttribute(key));
};

/**
 * return list of the node's attributes.
 * @memberof UI
 * @alias getAttributes
 * @param {HTMLElement} element
 * @returns {Record<string, boolean | string | number | null>}
 */
const getAttributes = (element: HTMLElement): Attributes => {
    return [...element.attributes].reduce<Attributes>((attributes, attr) => {
        if (BooleanAttributes.includes(attr.name)) {
            attributes[attr.name] =
                attr.nodeValue === 'true' ||
                attr.nodeValue === '' ||
                attr.nodeValue === attr.name;
        } else {
            attributes[attr.name] = attr.nodeValue;
        }
        return attributes;
    }, {});
};

/**
 * Updates element by given config.
 * @memberof UI
 * @alias updateElement
 * @param {HTMLElement | UIElement} element
 * @param {ElementConfig} config
 * @param {boolean} [trusted] - defines if element's strings are trusted HTML
 * @returns {HTMLElement}
 */
const updateElement = (
    element: HTMLElement | UIElement | Element,
    config: ElementConfig,
    trusted: boolean = false
): HTMLElement | Element => {
    if (config.classList) {
        updateClassList(element, config.classList);
    }
    if (config.attributes) {
        const attrs = config.attributes as Record<string, unknown>;
        Object.keys(attrs).forEach((key) => {
            if (attrs[key] && typeof attrs[key] === 'object') {
                config.props ??= {};
                config.props[key] = attrs[key];
                delete attrs[key];
            }
        });
        setAttributes(element, config.attributes as PropsAttribute);
    }

    if (
        config.hasOwnProperty('innerHTML') &&
        element.innerHTML !== config.innerHTML
    ) {
        console.warn('Usage of innerHTML is deprecated, use children instead');
        if (config.innerHTML) {
            setUnsafeInnerHTML(element, config.innerHTML);
        }
    }

    const items: UndefinedOr<Array<ElementChild>> = config.children && [
        ...config.children,
    ];
    if (Array.isArray(items)) {
        items
            .filter((item) => !!item)
            .forEach(
                /**
                 * @param {ElementConfigChildren} entry
                 */
                (entry: ElementChild) => {
                    []
                        .concat(entry as never)
                        .forEach((item: ElementChild | string | number) => {
                            if (
                                typeof item === 'string' ||
                                typeof item === 'number'
                            ) {
                                if (trusted) {
                                    const unsafeHTML = element.innerHTML + item;
                                    setUnsafeInnerHTML(element, unsafeHTML);
                                } else {
                                    element.appendChild(
                                        document.createTextNode(String(item))
                                    );
                                }
                            } else {
                                const child = isNode(item)
                                    ? item
                                    : createElement(
                                          item as ElementConfig,
                                          trusted
                                      );
                                if (child) {
                                    element.appendChild(child);
                                }
                            }
                        });
                }
            );
    }

    if (config.events) {
        Object.keys(config.events).forEach((key) => {
            if (typeof config.events![key] === 'function') {
                element.addEventListener(key, config.events![key]);
            }
        });
    }

    if (config.props) {
        Object.keys(config.props).forEach((key) => {
            // @ts-expect-error - attempt to assign to readonly properties
            element[key as keyof Element] = config.props![key];
        });
    }

    return element;
};

/**
 * Creates element by given tagName and config.
 * @memberof UI
 * @alias createElement
 * @param {ElementConfig} config
 * @param {boolean} [trusted] - defines if string should be parsed as HTML
 * @returns {UIElement | HTMLElement | null}
 */
const createElement = <T extends Element>(
    config: ElementConfig,
    trusted: boolean = false
): NullOr<T> => {
    let element: UndefinedOr<Element> = undefined;
    if (config.element instanceof HTMLElement) {
        element = config.element;
    } else if (config.tagName) {
        if (SVGElements.includes(config.tagName)) {
            element = document.createElementNS(
                resolveNamespace('svg'),
                config.tagName
            );
        } else {
            element = document.createElement(config.tagName);
        }
    }
    if (!element) {
        console.warn(
            'Invalid config given. Either tagName or element property required'
        );
        return null;
    }
    updateElement(element, config, trusted);
    return element as T;
};

/**
 * Inserts elements inside component.
 * @memberof UI
 * @alias insertElements
 * @param {Element|DocumentFragment} parent
 * @param {Array<ElementConfig | HTMLElement | string>} configs
 * @param {InsertPosition} [position]
 * @param {boolean} [trusted] - defines if element's strings are trusted HTML
 * @returns {Element}
 */
const insertElements = (
    parent: Element | DocumentFragment,
    configs: Array<
        Optional<ElementConfig | HTMLElement | DocumentFragment | Text | string>
    >,
    position: InsertPosition = 'beforeend',
    trusted: boolean = false
): Element => {
    const elements = configs
        .reduce<Array<HTMLElement | string>>((collection, item) => {
            return collection.concat(
                []
                    .concat(item as never)
                    .filter((item) => ![undefined, null, false].includes(item))
            );
        }, [])
        .map((item) => {
            if (typeof item === 'string' || isNode(item)) {
                return item;
            }
            return createElement(item, trusted);
        });
    const items = ['beforebegin', 'afterbegin'].includes(position)
        ? elements.reverse()
        : elements;
    items.forEach(
        /** @param {HTMLElement | string | number | boolean} element */
        (element: string | number | boolean | HTMLElement | Element | null) => {
            if (typeof element === 'string') {
                if (trusted) {
                    const template = document.createElement('template');
                    setUnsafeInnerHTML(template, element);
                    parent.appendChild(template.content);
                } else {
                    parent.appendChild(document.createTextNode(element));
                }
            } else if (
                typeof element === 'number' ||
                typeof element === 'boolean'
            ) {
                parent.appendChild(document.createTextNode(String(element)));
            } else {
                if (element) {
                    parent.appendChild(element as HTMLElement);
                }
            }
        }
    );
    return parent as Element;
};

/**
 * Recursively pre-render Element tree.
 * @memberof UI
 * @alias prerenderElementsTree
 * @param {Node} node
 * @param {boolean} [recursive]
 */
const prerenderElementsTree = (node: Node, recursive: boolean = false) => {
    // Do not do "'render' in node" check here, might break some pages.
    if (
        typeof (node as UIElement).render === 'function' &&
        ![ComponentState.Rendered, ComponentState.Hydrated].includes(
            (node as UIElement).state
        )
    ) {
        // @ts-expect-error - attempt to access private
        if (typeof node.runLifecycleHook === 'function') {
            // @ts-expect-error - attempt to access private
            node.runLifecycleHook('render');
        }
        (node as UIElement).state = ComponentState.Rendered;
    }
    if (!recursive) {
        return;
    }
    if (isElementNode(node)) {
        [].forEach.call(node.children, (child) =>
            prerenderElementsTree(child, recursive)
        );
    }
};

/*
 * Attribute and properties name converter. Converts property to attribute,
 * and vice-versa. By UI Team conventions, attribute name with kebab-case and
 * properties in camelCase.
 * https://uidocs.swedbank.net/conventions-code-style.html#attributes
 */
const nameConverter = {
    /**
     * @param {string} propertyName
     * @returns {string}
     */
    propToAttribute(propertyName: string): string {
        return propertyName.replace(
            /([A-Z])/g,
            (matches) => '-' + matches[0].toLowerCase()
        );
    },
    /**
     * @param {string} attributeName
     * @returns {string}
     */
    attrToProperty(attributeName: string): string {
        return attributeName.replace(/-([a-z])/g, (matches) =>
            matches[1].toUpperCase()
        );
    },
};

/**
 * Be careful when using this method,it can cause XSS vulnerabilities if used with untrusted input.
 * If you are using this method, make sure that the HTML string you are passing is from a trusted source and
 * does not contain any malicious code. Always sanitize user input before using it in this method to
 * prevent security issues. You are responsible for ensuring the safety of the HTML content you
 * are inserting into the DOM using this method.
 * @param {Element} element
 * @param {string} html
 */
const setUnsafeInnerHTML = (element: Element, html: string): Element => {
    // eslint-disable-next-line no-restricted-syntax
    element.innerHTML = html;
    return element;
};

export {
    createElement,
    insertElements,
    setAttributes,
    getAttributes,
    removeAttributes,
    setInnerText,
    setUnsafeInnerHTML,
    updateElement,
    updateClassList,
    revertClassList,
    prerenderElementsTree,
    resolveNamespace,
    nameConverter,
};
